import { Button, Dialog, DialogClose, DialogRef } from "@hellocarbo/lasagne";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Blocker } from "react-router";

import { PortalContainerContext } from "@/contexts";

type ConfirmLeaveModalProps = {
  blocker: Blocker;
  show: boolean;
};

const ConfirmLeaveModal = ({ blocker, show }: ConfirmLeaveModalProps) => {
  const { container } = useContext(PortalContainerContext);
  const { t } = useTranslation("externalCampaign");
  const dialogRef = useRef<DialogRef>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  // we need to add this condition as a typeguard since blocker.reset and blocker.proceed exist only when state is blocked
  if (blocker.state === "blocked") {
    if (show) {
      dialogRef?.current?.open();
    } else {
      blocker.proceed(); // since the conditions are not met, we resume the navigation by calling proceed
    }
  }

  // When the Dialog is closed through the confirmation button "leaveCta", we resume the navigation by calling proceed
  // When the Dialog is closed any other way (by escape, click outside, X cross, or the button "stayCta"),
  // we undo the navigation by calling reset
  const handleClose = () => {
    if (blocker.state === "blocked") {
      if (isConfirmed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  };

  return (
    <Dialog
      title={t("confirmModal.title")}
      ref={dialogRef}
      description={t("confirmModal.description")}
      portalContainer={container as Element}
      onClose={handleClose}
      footer={
        <>
          <DialogClose asChild>
            <Button
              shape="link"
              intent="primary"
              onPointerDown={() => setIsConfirmed(true)} // We use onPointerDown to be extra sure isConfirmed is updated before handleClose kicks in
            >
              {t("confirmModal.leaveCta")}
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button shape="solid" intent="primary">
              {t("confirmModal.stayCta")}
            </Button>
          </DialogClose>
        </>
      }
    />
  );
};

export default ConfirmLeaveModal;
